import { createContext, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import analyticsApiClient from "analytics-api-client";
import { useAppError } from "hooks/useAppError";
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const [token, setToken] = useLocalStorage("authToken", null);
  // 2FA challenge from the password step; component state only.
  const [challenge, setChallenge] = useState(null);

  const { notifyError } = useAppError();

  const navigate = useNavigate();

  const completeLogin = async (authData) => {
    setToken(authData.access_token);
    analyticsApiClient.init();

    const profileData = await analyticsApiClient.get_profile();
    setUser(profileData);
    setChallenge(null);

    if (profileData.clientId != 1) {
      navigate("/dashboard/reports", { replace: true });
    } else {
      navigate("/dashboard/tenants", { replace: true });
    }
  };

  const login = async (data) => {
    try {
      const authData = await analyticsApiClient.auth(
        data.username,
        data.password,
      );

      // Identity requires a second factor: hold the challenge for the code step.
      if (authData?.requires_two_factor) {
        setChallenge(authData);
        return;
      }

      await completeLogin(authData);
    } catch (error) {
      notifyError(error, {
        fallbackMessage: "Login failed. Please try again.",
      });
    }
  };

  const verifyTwoFactor = async ({ code, method }) => {
    try {
      const authData = await analyticsApiClient.verify2fa({
        twoFactorToken: challenge.two_factor_token,
        code,
        method: method || challenge.two_factor_method || "Authenticator",
      });
      await completeLogin(authData);
    } catch (error) {
      notifyError(error, {
        fallbackMessage: "Invalid or expired code. Please try again.",
      });
    }
  };

  const cancelChallenge = () => setChallenge(null);

  const logout = () => {
    setUser(null);
    setChallenge(null);
    navigate("/", { replace: true });
  };

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      challenge,
      verifyTwoFactor,
      cancelChallenge,
    }),
    [user, challenge],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
